import { useEffect, useState } from 'react';

/**
 * Reveals an element the first time it scrolls into view.
 *
 * Deliberately not framer-motion. That library is roughly 50KB gzipped on top
 * of React, and this site's visitors are farm owners in Kenya and Uganda on
 * mobile data -- the whole JS bundle is currently 46KB gzipped, so adding it
 * would more than double the download to animate a fade and a rise. Everything
 * the design actually asks for is a transform and an opacity, which the
 * compositor handles natively for free.
 *
 * A spring physics library earns its weight when there is drag, gesture or
 * layout animation to do. There is none here. Revisit if that changes.
 *
 * Reveals once and then disconnects: re-animating on every scroll past is
 * distracting, and it keeps the observer count flat on a long page.
 */
export function useReveal({ threshold = 0.15, rootMargin = '0px 0px -60px 0px' } = {}) {
  /*
   * A callback ref, not useRef. Sections that wait on CMS data render nothing
   * on the first pass, so a plain ref is still null when the effect runs, and
   * nothing in the dependency list changes once the data lands — the observer
   * would never attach and the content would sit at opacity 0 forever.
   * Tracking the node in state re-runs the effect the moment it mounts.
   */
  const [el, setEl] = useState(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!el || shown) return;

    // Anyone who has asked for less motion gets the finished state directly,
    // and no observer at all.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }

    // Old browsers without IntersectionObserver simply see the content.
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [el, shown, threshold, rootMargin]);

  return [setEl, shown];
}

/**
 * Props for a standard rise-and-fade. Spread onto the element.
 *
 * The stagger is an inline style rather than an arbitrary Tailwind class:
 * Tailwind generates CSS by scanning source for literal class strings, so a
 * class built at runtime from a variable produces markup with no rule behind
 * it. Every child would then transition together and the stagger would
 * silently do nothing.
 */
export function reveal(shown, delay = 0) {
  return {
    className: [
      'transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none',
      shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
    ].join(' '),
    style: delay ? { transitionDelay: `${delay}ms` } : undefined,
  };
}
